import {ReportRequestGate,coalesceRefresh} from './refresh-control.ts';
import {reportDatePresets,isReportDateRange} from './date-range.ts';
import {reportDeliveryStatus,applyDeliveryTransitions} from '../../../../shared/report-delivery.ts';
import type { AutomationSummary } from '../../../../shared/triggers/report.ts';
import { useServerStatsSync } from './useServerStatsSync.ts';
import { isCampaignBadge, reportIdentity, creativeToken, creativeId, creativeTokenFromId, type ReportIdentity } from '../../../../shared/report-identity.ts';
import { usePersistentState } from '../../core/usePersistentState.ts';
// modules/ads-stats/useAdsStats.ts
// Hook для управління звітом по оголошеннях

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { api, ApiClientError } from '../../core/apiClient.ts';
import { useToast } from '../../core/contexts/ToastContext.tsx';

export type TabKey = 'ads' | 'geo' | 'accounts' | 'campaigns' | 'creative';

// ─── Types ─────────────────────────────────────────────────────

interface LegacyReportFields {
  metrics_state?:'complete'|'partial'|'unknown';
  metrics_observed_at?:string|null;
  detail_state?:string;
  accountOwners?: {accountId:string;id:string;name:string|null;accountName?:string|null;timezone?:string|null;currency?:string|null}[];
  campaignBadges?: string[];
  geoCountries?: string[];
  socialAccounts?: {id:string;name:string|null}[];
  automation?:AutomationSummary;
  ad_id: string;
  ad_name: string | null;
  ad_status: string | null;
  campaign_name: string | null;
  adset_name: string | null;
  ad_account_id: string;
  ad_account_name: string | null;
  currency: string | null;
  account_timezone?: string | null;
  budget_currency?: string | null;
  budget_usd_rate?: number;
  budget_offset?: number;
  spend: number;
  impressions: number;
  clicks: number;
  cpm: number;
  cpc: number;
  ctr: number;
  leads: number;
  cost_per_lead: number;
  app_installs: number;
  cost_per_app_install: number;
  registrations: number;
  cost_per_registration: number;
  purchases: number;
  cost_per_purchase: number;
  campaign_budget?: number | null;
  campaign_budget_type?: string | null;
  adset_budget?: number | null;
  adset_budget_type?: string | null;
  adset_id?: string | null;
  campaign_id?: string | null;
  // creative tab
  creative?: string | null;
  creative_id?: string|null;
  creative_token?: string|null;
  campaign_fallback_ad_id?: string|null;
  campaign_counts?: { active: number; paused: number; banned: number; rejected?:number; scheduled?:number; preparing?:number; notDelivering?:number; unknown?:number };
}

export type AdsReportRow = LegacyReportFields & ReportIdentity;

export interface AdInsightSyncLog {
  id: number;
  date: string;
  accountsRequested: number | null;
  accountsSuccess: number | null;
  accountsFailed: number | null;
  durationMs: number | null;
  errorDetails: string | null;
  createdAt: string;
}

export type SortField = keyof AdsReportRow;
export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

export interface DateRange {
  from: string;
  to: string;
}

export interface MetricFilter {
  field: string;
  operator: '>=' | '<=' | '=';
  value: number;
}

// ─── Date helpers ───────────────────────────────────────────────

export function getPresets(): Record<string, DateRange> {
  return reportDatePresets();
}

// ─── localStorage ───────────────────────────────────────────────

const LS_ACCOUNT_FILTER_KEY = 'adsStatsAccountFilter';
const LS_DATE_RANGE_KEY = 'adsStatsDateRange';
const LS_STATUS_FILTER_KEY = 'adsStatsStatusFilter';
const LS_METRIC_FILTERS_KEY = 'adsStatsMetricFilters';
const LS_TAG_FILTERS_KEY = 'adsStatsTagFilters';

function loadFilterFromStorage(): string[] | null {
  try {
    const raw = localStorage.getItem(LS_ACCOUNT_FILTER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch { return null; }
}

function saveFilterToStorage(ids: string[] | null): void {
  try {
    if (ids && ids.length > 0) {
      localStorage.setItem(LS_ACCOUNT_FILTER_KEY, JSON.stringify(ids));
    } else {
      localStorage.removeItem(LS_ACCOUNT_FILTER_KEY);
    }
  } catch { /* ignore */ }
}

function loadDateRangeFromStorage(): DateRange | null {
  try {
    const raw = localStorage.getItem(LS_DATE_RANGE_KEY);
    if (!raw) return null;
    const parsed:unknown=JSON.parse(raw);return isReportDateRange(parsed)?parsed:null;
  } catch { return null; }
}

function saveDateRangeToStorage(range: DateRange): void {
  try {
    localStorage.setItem(LS_DATE_RANGE_KEY, JSON.stringify(range));
  } catch { /* ignore */ }
}

function loadStatusFilterFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(LS_STATUS_FILTER_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveStatusFilterToStorage(filter: string[]): void {
  try { localStorage.setItem(LS_STATUS_FILTER_KEY, JSON.stringify(filter)); } catch { }
}

function loadMetricFiltersFromStorage(): MetricFilter[] {
  try {
    const raw = localStorage.getItem(LS_METRIC_FILTERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveMetricFiltersToStorage(filters: MetricFilter[]): void {
  try { localStorage.setItem(LS_METRIC_FILTERS_KEY, JSON.stringify(filters)); } catch { }
}

function loadTagFiltersFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(LS_TAG_FILTERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveTagFiltersToStorage(tags: string[]): void {
  try { localStorage.setItem(LS_TAG_FILTERS_KEY, JSON.stringify(tags)); } catch { }
}

// ─── Sorting ────────────────────────────────────────────────────

function sortRows(rows: AdsReportRow[], config: SortConfig): AdsReportRow[] {
  return [...rows].sort((a, b) => {
    const aVal = a[config.field];
    const bVal = b[config.field];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    let cmp: number;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      cmp = aVal - bVal;
    } else {
      cmp = String(aVal).localeCompare(String(bVal), 'uk');
    }
    return config.direction === 'desc' ? -cmp : cmp;
  });
}

// ─── Hook ───────────────────────────────────────────────────────

export function useAdsStats() {
  const { showToast } = useToast();
  const [deliveryNow,setDeliveryNow]=useState(()=>Date.now());

  const presets = getPresets();
  const defaultRange = loadDateRangeFromStorage() ?? presets.yesterday;

  const [dateRange, setDateRangeState] = useState<DateRange>(defaultRange);
  const [activeTab, setActiveTab] = usePersistentState<TabKey>('stats.tab', 'campaigns', (v): v is TabKey => ['ads', 'geo', 'accounts', 'campaigns', 'creative'].includes(v as string));

  const [reports, setReports] = useState<{
    ads: AdsReportRow[] | null;
    geo: any[] | null;
    accounts: any[] | null;
    campaigns: any[] | null;
    creative: any[] | null;
  }>({
    ads: null,
    geo: null,
    accounts: null,
    campaigns: null,
    creative: null,
  });

  const [filterOptions,setFilterOptions]=useState<{campaignBadges:string[];socialAccounts:{id:string;name:string|null}[]}>({campaignBadges:[],socialAccounts:[]});
  const [loading, setLoading] = useState(false);
  const [reportErrors, setReportErrors] = useState<Partial<Record<TabKey, string>>>({});
  const [lastSync, setLastSync] = useState<AdInsightSyncLog | null>(null);
  const [needsWide,setNeedsWide]=useState<boolean|null>(null);
  const manifestRequest=useRef(0);

  const [accountFilter, setAccountFilterState] = useState<string[] | null>(
    () => loadFilterFromStorage()
  );

  const setAccountFilter = useCallback((ids: string[] | null) => {
    const normalized = ids && ids.length > 0 ? ids : null;
    setAccountFilterState(normalized);
    saveFilterToStorage(normalized);
  }, []);

  const setDateRange = useCallback((range: DateRange) => {
    setDateRangeState(range);
    saveDateRangeToStorage(range);
  }, []);

  const [sortConfig, setSortConfig] = usePersistentState<SortConfig>('stats.sort', {
    field: 'spend',
    direction: 'desc',
  });

  const [entitySearch, setEntitySearch] = usePersistentState('stats.search', '');
  const [tabSearch, setTabSearch] = usePersistentState('stats.tab-search', {creative:'',geo:'',accounts:''});
  const entityTab = activeTab === 'campaigns' || activeTab === 'ads';
  const searchQuery = entityTab ? entitySearch : tabSearch[activeTab as keyof typeof tabSearch];
  const [creativeFilter,setCreativeFilter] = usePersistentState<string|null>('stats.creative-filter',null,(v):v is string|null=>v===null||(typeof v==='string'&&creativeTokenFromId(v)!==null));
  const setSearchQuery = useCallback((value:string) => {
    if(activeTab==='campaigns' || activeTab==='ads') {setEntitySearch(value);setCreativeFilter(null);}
    else setTabSearch(previous=>({...previous,[activeTab]:value}));
  },[activeTab,setEntitySearch,setCreativeFilter,setTabSearch]);
  const stringList = (value: unknown): value is string[] => Array.isArray(value) && value.every(item => typeof item === 'string');
  const [campaignBadgeFilter,setCampaignBadgeFilter] = usePersistentState<string[]>('stats.campaign-badge-filter',[],(value): value is string[] => stringList(value) && value.every(badge => isCampaignBadge(badge)));
  const [socialBadgeFilter,setSocialBadgeFilter] = usePersistentState<string[]>('stats.social-badge-filter',[],stringList);

  // ── Tag filters (multi-contains, AND) ────────────────────────
  const [tagFilters, setTagFiltersState] = useState<string[]>(
    () => loadTagFiltersFromStorage()
  );
  const setTagFilters = useCallback((tags: string[]) => {
    setTagFiltersState(tags);
    saveTagFiltersToStorage(tags);
  }, []);

  const addTagFilter = useCallback((tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    setTagFiltersState((prev) => {
      // Не дублювати
      if (prev.some((t) => t.toLowerCase() === trimmed.toLowerCase())) return prev;
      const next = [...prev, trimmed];
      saveTagFiltersToStorage(next);
      return next;
    });
  }, []);

  const removeTagFilter = useCallback((tag: string) => {
    setTagFiltersState((prev) => {
      const next = prev.filter((t) => t !== tag);
      saveTagFiltersToStorage(next);
      return next;
    });
  }, []);

  const [statusFilter, setStatusFilterState] = useState<string[]>(
    () => loadStatusFilterFromStorage()
  );
  const setStatusFilter = useCallback((filter: string[]) => {
    setStatusFilterState(filter);
    saveStatusFilterToStorage(filter);
  }, []);

  const [metricFilters, setMetricFiltersState] = useState<MetricFilter[]>(
    () => loadMetricFiltersFromStorage()
  );
  const setMetricFilters = useCallback((filters: MetricFilter[]) => {
    setMetricFiltersState(filters);
    saveMetricFiltersToStorage(filters);
  }, []);

  // ── Geo filter ────────────────────────────────────────────────
  const [geoFilter] = useState<string[]>([]);

  // Advance scheduled labels at their start time using cached data only.
  useEffect(()=>{
    const starts=Object.values(reports).flatMap(rows=>(rows??[]).flatMap((row:any)=>[row.delivery_start_at,...(row.delivery_transitions??[]).map((item:any)=>item.at)]))
      .map(value=>Date.parse(value??'')).filter(value=>Number.isFinite(value)&&value>deliveryNow);
    if(!starts.length)return;
    const timer=setTimeout(()=>setDeliveryNow(Date.now()),Math.min(2147483647,Math.max(0,Math.min(...starts)-Date.now())+10));
    return ()=>clearTimeout(timer);
  },[reports,deliveryNow]);

  // Normalize active report for the table — wrapped in useMemo to guarantee re-render after sync
  const activeReportNormalized = useMemo((): AdsReportRow[] => {
    const raw = reports[activeTab];
    if (!raw) return [];
    return raw.map((d:any) => ({
      ...d,
      ...reportIdentity(activeTab,d),
      // Display aliases are kept for the existing table/export adapters; never
      // use ad_id as a campaign/creative/geo/account mutation identity.
      ad_id: activeTab==='ads' ? d.ad_id : '',
      ad_name: activeTab==='campaigns'?d.campaign_name:activeTab==='creative'?d.creative:activeTab==='geo'?d.country:activeTab==='accounts'?d.ad_account_name||d.ad_account_id:d.ad_name,
      campaign_id:d.campaign_id??null,ad_status:reportDeliveryStatus(d.delivery_status??d.ad_status??null,d,Math.max(deliveryNow,Date.now())),
      campaign_counts:d.campaign_counts?applyDeliveryTransitions(d.campaign_counts,d.delivery_transitions??[],Math.max(deliveryNow,Date.now())):undefined,
      campaign_name:d.campaign_name??null,ad_account_id:d.ad_account_id??'',adset_name:d.adset_name??null,
      ad_account_name:d.ad_account_name??null,currency:d.currency??null,
      leads:d.metrics_state==='unknown'?null:d.leads??0,cost_per_lead:d.metrics_state==='unknown'?null:d.cost_per_lead??0,app_installs:d.metrics_state==='unknown'?null:d.app_installs??0,
      cost_per_app_install:d.metrics_state==='unknown'?null:d.cost_per_app_install??0,registrations:d.metrics_state==='unknown'?null:d.registrations??0,
      cost_per_registration:d.metrics_state==='unknown'?null:d.cost_per_registration??0,purchases:d.metrics_state==='unknown'?null:d.purchases??0,cost_per_purchase:d.metrics_state==='unknown'?null:d.cost_per_purchase??0,
      creative:d.creative??null,creative_id:d.creative_id??null,
    } as AdsReportRow));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, activeTab,deliveryNow]);

  // Apply filters
  const filteredReport = activeReportNormalized.filter((row) => {
    {
      if (campaignBadgeFilter.length && !row.campaignBadges?.some(badge => campaignBadgeFilter.includes(badge))) return false;
      if (socialBadgeFilter.length && !row.socialAccounts?.some(account => socialBadgeFilter.includes(account.id))) return false;
    }
    if(creativeFilter && ['campaigns','ads'].includes(activeTab)) {
      const token=row.creative_token??(activeTab==='creative'?row.creative:creativeToken(row.campaign_name));
      if(!token || creativeId(token)!==creativeFilter)return false;
    }
    // 1. Live search (while typing)
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        (row.campaign_name?.toLowerCase().includes(q)) ||
        (row.ad_name?.toLowerCase().includes(q)) ||
        (row.creative_token?.toLowerCase().includes(q)) ||
        (row.ad_account_id?.toLowerCase().includes(q));
      if (!matchesSearch) return false;
    }

    // 2. Tag filters (multi-contains, AND — рядок повинен містити ВСІ теги)
    if (tagFilters.length > 0) {
      for (const tag of tagFilters) {
        const t = tag.toLowerCase();
        const matchesTag =
          (row.campaign_name?.toLowerCase().includes(t)) ||
          (row.ad_name?.toLowerCase().includes(t)) ||
          (row.ad_account_id?.toLowerCase().includes(t));
        if (!matchesTag) return false;
      }
    }

    // 3. Status Filter
    if (statusFilter.length > 0) {
      if (!row.ad_status || !statusFilter.includes(row.ad_status)) {
        return false;
      }
    }

    // 4. Metric Filters
    if (metricFilters.length > 0) {
      if(row.metrics_state==='unknown')return false;
      for (const filter of metricFilters) {
        const rowVal = Number(row[filter.field as keyof AdsReportRow] ?? 0);
        if (filter.operator === '>=') {
          if (rowVal < filter.value) return false;
        } else if (filter.operator === '<=') {
          if (rowVal > filter.value) return false;
        } else if (filter.operator === '=') {
          if (rowVal !== filter.value) return false;
        }
      }
    }

    return true;
  });

  const sortedReport = sortRows(filteredReport, sortConfig);

  // ── Fetch report ─────────────────────────────────────────────

  const reportGate=useRef(new ReportRequestGate());
  const loadingRequests=useRef(0);
  const forceFreshReport=useRef(false);
  const invalidateReports=useCallback((forceFresh=true)=>{if(forceFresh)forceFreshReport.current=true;reportGate.current.invalidate();setReports({ads:null,geo:null,accounts:null,campaigns:null,creative:null});},[]);
  const fetchReport = useCallback(
    async (range: DateRange, accIds: string[] | null, tab: TabKey, geoCountries: string[], throwOnError = false,background=false,forceFresh=false) => {
      const isCurrent=reportGate.current.begin(tab);
      if(!background){loadingRequests.current++;setLoading(true);}
      try {
        let endpoint = '/api/ads-stats/report/ads';
        if (tab === 'geo') endpoint = '/api/ads-stats/report/geo';
        if (tab === 'accounts') endpoint = '/api/ads-stats/report/accounts';
        if (tab === 'campaigns') endpoint = '/api/ads-stats/report/campaigns';
        if (tab === 'creative') endpoint = '/api/ads-stats/report/creative';

        let url = `${endpoint}?date_from=${encodeURIComponent(range.from)}&date_to=${encodeURIComponent(range.to)}`;
        if (accIds && accIds.length > 0) {
          url += `&account_ids=${encodeURIComponent(accIds.join(','))}`;
        }
        if (geoCountries.length > 0) {
          url += `&countries=${encodeURIComponent(geoCountries.join(','))}`;
        }
        if(campaignBadgeFilter.length)url+='&campaign_badges='+encodeURIComponent(JSON.stringify(campaignBadgeFilter));
        if(socialBadgeFilter.length)url+='&social_account_ids='+encodeURIComponent(JSON.stringify(socialBadgeFilter));
        const fresh=forceFresh||forceFreshReport.current;
        forceFreshReport.current=false;
        const data = await api.get<any[]>(url,{freshReport:fresh});
        if(!isCurrent())return data;
        setFilterOptions(prev=>({campaignBadges:[...new Set([...prev.campaignBadges,...data.flatMap(row=>row.campaignBadges??[])])],
          socialAccounts:[...new Map([...prev.socialAccounts,...data.flatMap(row=>row.socialAccounts??[])].map(account=>[account.id,account])).values()]}));
        setReportErrors(prev => ({ ...prev, [tab]: '' }));
        setReports(prev => ({ ...prev, [tab]: data }));
        return data;
      } catch (err) {
        if(!isCurrent()){if(throwOnError)throw err;return [];}
        const msg = err instanceof ApiClientError ? err.message : 'Не вдалося завантажити звіт';
        setReportErrors(prev => ({ ...prev, [tab]: msg }));
        showToast(msg, 'error');
        setReports(prev => ({ ...prev, [tab]: prev[tab] ?? [] }));
        if (throwOnError) throw err;
        return [];
      } finally {
        if(!background){loadingRequests.current--;setLoading(loadingRequests.current>0);}
      }
    },
    [showToast,campaignBadgeFilter,socialBadgeFilter]
  );

  // ── Fetch last sync log ───────────────────────────────────────

  const fetchLastSync = useCallback(async (range: DateRange) => {
    const observation=++manifestRequest.current;
    setLastSync(null);setNeedsWide(null);
    const query=`date_from=${range.from}&date_to=${range.to}${accountFilter?.length?`&account_ids=${accountFilter.join(',')}`:''}`;
    void api.get<{needsWide:boolean}>(`/api/ads-stats/manifest?${query}`).then(result=>{if(observation===manifestRequest.current)setNeedsWide(result.needsWide);}).catch(()=>{if(observation===manifestRequest.current)setNeedsWide(null);});
    try {
      const dateKey = range.from === range.to ? range.from : `${range.from}..${range.to}`;
      const log = await api.get<AdInsightSyncLog | null>(
        `/api/ads-stats/sync-logs/last?date=${encodeURIComponent(dateKey)}`
      );
      if(observation===manifestRequest.current)setLastSync(log);
    } catch {
      if(observation===manifestRequest.current)setLastSync(null);
    }
  }, [accountFilter]);

  // ── Auto-load on range/filter/geoFilter change ────────────────

  useEffect(()=>{
    let stopped=false;
    let url='/api/ads-stats/filter-options?date_from='+dateRange.from+'&date_to='+dateRange.to;
    if(accountFilter?.length)url+='&account_ids='+encodeURIComponent(accountFilter.join(','));
    void api.get<{campaignBadges:string[];socialAccounts:{id:string;name:string|null}[]}>(url).then(options=>{
      if(!stopped&&Array.isArray(options.campaignBadges)&&Array.isArray(options.socialAccounts))setFilterOptions(options);
    }).catch(()=>{});
    return ()=>{stopped=true;};
  },[dateRange,accountFilter]);

  const prevRangeRef = useRef<string>('');
  const prevFilterRef = useRef<string | null>(null);
  const prevGeoRef = useRef<string>('');
  const prevSourceRef=useRef<string>('');

  useEffect(() => {
    const rangeKey = `${dateRange.from}..${dateRange.to}`;
    const filterKey = accountFilter ? accountFilter.join(',') : null;
    const geoKey = geoFilter.join(',');
    const sourceKey=JSON.stringify([campaignBadgeFilter,socialBadgeFilter]);

    if (
      rangeKey !== prevRangeRef.current ||
      filterKey !== prevFilterRef.current ||
      geoKey !== prevGeoRef.current || sourceKey !== prevSourceRef.current
    ) {
      prevRangeRef.current = rangeKey;
      prevFilterRef.current = filterKey;
      prevGeoRef.current = geoKey;prevSourceRef.current=sourceKey;

      invalidateReports(false);
      setReportErrors({});
      fetchReport(dateRange, accountFilter, activeTab, geoFilter);
      fetchLastSync(dateRange);
    } else {
      if (reports[activeTab] === null && !loading) {
        fetchReport(dateRange, accountFilter, activeTab, geoFilter);
      }
    }
  }, [dateRange, accountFilter, geoFilter, activeTab, fetchReport, fetchLastSync, reports, loading,invalidateReports]);

  // ── Fetch available countries (without geo filter) on range/accounts change ──
  const fetchAllReports = async () => {
    // Fetch each exported report once; never turn a missing rate into an empty export.
    const [ads, geo, accounts, campaigns] = await Promise.all(
      (['ads', 'geo', 'accounts', 'campaigns'] as const).map(tab =>
        reports[tab] !== null && !reportErrors[tab]
          ? Promise.resolve(reports[tab]!)
          : fetchReport(dateRange, accountFilter, tab, geoFilter, true)
      )
    );
    return { ads, geo, accounts, campaigns };
  };

  // ── Sync ─────────────────────────────────────────────────────

  const refreshCallback=useRef(()=>{});
  refreshCallback.current=()=>{forceFreshReport.current=true;invalidateReports();void fetchLastSync(dateRange);};
  const refreshController=useRef<ReturnType<typeof coalesceRefresh>>();
  if(!refreshController.current)refreshController.current=coalesceRefresh(()=>refreshCallback.current());
  useEffect(()=>()=>refreshController.current?.cancel(),[]);
  const onServerSyncComplete=useCallback(()=>refreshController.current?.request(),[]);
  const {syncing,syncProgress,evaluationPending,startSync,cancelSync,cancelling,cancelRequested}=useServerStatsSync(onServerSyncComplete);
  const openedCampaigns=useRef(new Set<string>());
  const setCampaignOpen=useCallback((id:string,open:boolean)=>{if(open)openedCampaigns.current.add(id);else openedCampaigns.current.delete(id);},[]);
  const sync=useCallback(async(selectedAccountIds?:string[],intent:'wide'|'sync'='sync')=>{
    const ids=selectedAccountIds?.length?selectedAccountIds:accountFilter?.length?accountFilter:undefined;
    await startSync({date_from:dateRange.from,date_to:dateRange.to,intent,opened_campaign_ids:[...openedCampaigns.current],...(ids?{account_ids:ids}:{})});
  },[dateRange,accountFilter,startSync]);

  // ── Toggle sort ───────────────────────────────────────────────

  const toggleSort = useCallback((field: SortField) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const patchReportRow = useCallback((tab: TabKey, matchField: string, matchValue: string, patch: Record<string, any>, accountId?:string) => {
    reportGate.current.invalidate();
    const nextPatch='ad_status' in patch?{...patch,delivery_status:patch.ad_status}:patch;
    setReports((prev) => {
      const list = prev[tab];
      if (!list) return prev;
      const next = list.map((row: any) => (row[matchField] === matchValue && (!accountId || row.ad_account_id===accountId) ? { ...row, ...nextPatch } : row));
      return { ...prev, [tab]: next };
    });
  }, []);

  // Keep the confirmed local mutation visible while its report is reloaded.
  const refreshAfterMutation = useCallback(async () => {
    reportGate.current.invalidate();
    setReports(prev => ({ads:null,geo:null,accounts:null,campaigns:null,creative:null,[activeTab]:prev[activeTab]}));
    await fetchReport(dateRange,accountFilter,activeTab,geoFilter,false,true,true);
    void fetchLastSync(dateRange);
  }, [activeTab,dateRange,accountFilter,geoFilter,fetchReport,fetchLastSync]);

  const openCreativeCampaigns = useCallback((creative: string) => {
    setCreativeFilter(creativeId(creative));
    setEntitySearch(creative);
    setActiveTab('campaigns');
  }, []);

  return {
    activeTab,
    setActiveTab,
    report: sortedReport,
    reportError: reportErrors[activeTab] ?? '',
    reports, filterOptions,
    fetchAllReports,
    loading,
    syncing,
    syncProgress,
    evaluationPending,
    cancelSync,
    cancelling,
    cancelRequested,
    lastSync,
    needsWide,
    dateRange,
    setDateRange,
    sync,
    setCampaignOpen,
    patchReportRow,
    refreshAfterMutation,
    invalidateReports,
    creativeFilter, setCreativeFilter,
    campaignBadgeFilter, setCampaignBadgeFilter,
    socialBadgeFilter, setSocialBadgeFilter,
    sortConfig,
    toggleSort,
    accountFilter,
    setAccountFilter,
    searchQuery,
    setSearchQuery,
    tagFilters,
    setTagFilters,
    addTagFilter,
    removeTagFilter,
    statusFilter,
    setStatusFilter,
    metricFilters,
    setMetricFilters,
    activeReportNormalized,
    openCreativeCampaigns,
  };
}
